import React, { PropsWithChildren } from 'react';
import { Box, Flex } from 'pounceV2';
import HeaderV3 from 'Components/Header/HeaderV3';
import storage from 'Helpers/storage';
import TopNavigationV3 from 'Components/Navigation/TopNavigation/TopNavigationV3';
import SideNavigationV3 from 'Components/Navigation/SideNavigation/SideNavigationV3';
import {
  SIDE_NAV_EXPANDED_STORAGE_KEY,
  COLLAPSED_SIDE_NAVIGATION_WIDTH,
  EXPANDED_SIDE_NAVIGATION_WIDTH,
} from 'Components/Navigation/constants';
import { usePageHeaderContextV3 } from 'Source/contexts/pageHeaderContext';
import { PageWidthSize, useGetPageWidth } from 'Components/Layout/utils';
import { useTheme, Theme } from '@mui/material';
import { AIPanelV3 } from 'Components/AIPanelV3';

const MAIN_CONTENT_SCROLLBAR_WIDTH_IN_PX = 8;
const STANDARD_PAGE_WIDTH = '1760px';

const getMaxPageWidth = (
  breakpoints: Theme['dimensions'],
  pageWidth: PageWidthSize | null,
): string => {
  if (pageWidth === PageWidthSize.MAX || pageWidth === PageWidthSize.XL) {
    return breakpoints.breakpointXl; // 3840px
  }
  return STANDARD_PAGE_WIDTH;
};

export const LayoutV3: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    dimensions,
    palette: { tokens },
  } = useTheme();
  const { isSticky } = usePageHeaderContextV3();
  const pageWidth = useGetPageWidth();
  const maxPageWidth = getMaxPageWidth(dimensions, pageWidth);

  const [isNavExpanded, setIsNavExpanded] = React.useState<boolean>(
    storage.local.read(SIDE_NAV_EXPANDED_STORAGE_KEY) ??
      window.innerWidth >= parseInt(maxPageWidth, 10),
  );
  const isMaxWidth = pageWidth === PageWidthSize.MAX;

  const mainContentHorizontalPaddingInPx = isMaxWidth ? 0 : 32;
  const mainContentStyles = { scrollbarGutter: isMaxWidth ? 'auto' : 'stable both-edges' };

  const navigationWidth = isNavExpanded
    ? EXPANDED_SIDE_NAVIGATION_WIDTH
    : COLLAPSED_SIDE_NAVIGATION_WIDTH;

  // To calculate the main content, we want to subtract all sibling elements of the main content and see what we have
  // left. We need to remove sidebar navigation width, paddings & scrollbar widths
  const mainContentWidthSx = React.useMemo(() => {
    // We subtract paddings (x2) and scrollbars (x2 since we have the "both edges" option in the MAIN_STYLES above
    const calcXs = `calc(${dimensions.breakpointXs} - ${navigationWidth}px - ${
      2 * mainContentHorizontalPaddingInPx
    }px - ${2 * MAIN_CONTENT_SCROLLBAR_WIDTH_IN_PX}px)`;
    const calcSm = `calc(${maxPageWidth} - ${navigationWidth}px - ${
      2 * mainContentHorizontalPaddingInPx
    }px - ${2 * MAIN_CONTENT_SCROLLBAR_WIDTH_IN_PX}px)`;

    return {
      // <= 980px (fixed content width)
      [`@media (max-width: ${dimensions.breakpointXs})`]: { width: calcXs },
      // > 980px && <= 1500px (fluid content width)
      [`@media (min-width: ${dimensions.breakpointXs}) and (max-width: ${dimensions.breakpointSm})`]:
        {
          width: '100%',
        },
      // >= 1500px (fixed content width)
      [`@media (min-width: ${dimensions.breakpointSm})`]: { width: calcSm },
    };
  }, [
    dimensions.breakpointXs,
    dimensions.breakpointSm,
    navigationWidth,
    mainContentHorizontalPaddingInPx,
    maxPageWidth,
  ]);

  return (
    <Flex
      height="100vh"
      width="100vw"
      position="relative"
      bg={tokens.background.neutral.canvas.base}
    >
      <SideNavigationV3 isNavExpanded={isNavExpanded} setIsNavExpanded={setIsNavExpanded} />
      <Flex
        direction="column"
        flex="0 1 100%"
        height="100%"
        ml={`${navigationWidth}px`}
        transition="margin 250ms ease"
        position="relative"
        overflow="hidden"
      >
        <TopNavigationV3 />
        <Box as="main" flex="1 1 auto" overflow="hidden" style={mainContentStyles}>
          <Flex
            direction="column"
            height="100%"
            overflow="auto"
            style={mainContentStyles}
            id="layout-scroll-container"
          >
            <Box flex="1 1 auto">
              <Box
                px={`${mainContentHorizontalPaddingInPx}px`}
                maxWidth="100%"
                mx="auto"
                position={isSticky ? 'relative' : undefined}
                height={isMaxWidth ? '100%' : 'auto'}
                sx={mainContentWidthSx}
              >
                <HeaderV3 />
                {children}
              </Box>
              <Box id="secondary-page-content" position="relative" />
            </Box>
            <Box id="footer" as="footer" width="100%" />
          </Flex>
        </Box>
      </Flex>
      <AIPanelV3 />
    </Flex>
  );
};
